<template>
  <UForm
    id="sign-form"
    :schema="signFormSchema"
    :state="state"
    class="gap-x-2 gap-y-2 grid grid-cols-2 mx-auto"
    @submit="signPetition"
  >
    <UFormField
      class="max-sm:col-span-2 sm:col-span-1"
      name="first_name"
    >
      <UInput
        v-model="state.first_name"
        :placeholder="t('forms.first_name')"
        icon="i-lucide-user"
        size="xl"
        type="text"
      />
    </UFormField>
    <UFormField
      class="max-sm:col-span-2 sm:col-span-1"
      name="last_name"
    >
      <UInput
        v-model="state.last_name"
        :placeholder="t('forms.last_name')"
        icon="i-lucide-users"
        size="xl"
        type="text"
      />
    </UFormField>

    <UFormField
      class="col-span-2"
      name="phone"
    >
      <UInput
        v-model="state.phone"
        :placeholder="t('forms.phone')"
        type="phone"
        icon="i-lucide-phone"
        size="xl"
      />
    </UFormField>

    <UFormField
      class="col-span-2"
      name="email"
    >
      <UInput
        v-model="state.email"
        :placeholder="t('forms.email')"
        icon="i-lucide-mail"
        type="email"
        size="xl"
      />
    </UFormField>
    <UButton
      v-if="suggestedEmail"
      color="neutral"
      variant="subtle"
      class="col-span-2 mx-auto"
      size="xl"
      @click="state.email = suggestedEmail"
    >
      {{ t('forms.didYouMean') }} {{ suggestedEmail }}?
    </UButton>

    <UFormField
      v-if="!disableNewsletterOption"
      class="flex flex-col items-start col-span-2 text-start"
      label="Kampanjenyheter"
      description="Engasjer deg ved å følge med på våre kampanjer og få informasjon om hvordan du kan hjelpe dyr."
      name="newsletter"
    >
      <USwitch
        v-model="state.newsletter"
        size="xl"
      />
    </UFormField>

    <UButton
      :loading="is_loading"
      :label="!is_loading ? t('forms.btnSign') : t('forms.btnWait')"
      class="justify-center col-span-2 font-semibold text-black"
      size="xl"
      type="submit"
    />
    <FormPrivacyNotice class="col-span-2" />
  </UForm>
</template>

<script lang="ts" setup>
import emailSpellChecker from '@zootools/email-spell-checker'
import { parsePhoneNumberWithError } from 'libphonenumber-js'
import { signFormSchema } from '#shared/utils/schemas'
import type { CreateUserSchema, SignFormSchema } from '#shared/utils/schemas'
import type { AnimaResponse } from '#shared/types'
import petition from '~~/content/no/petition'

const { data } = getData()

const is_loading = ref(false)
const { query } = useRoute()
const user = useUserStore()

const state = ref<Partial<SignFormSchema>>({
  first_name: undefined,
  last_name: undefined,
  email: undefined,
  phone: undefined,
  newsletter: true,
})

const props = defineProps({
  sfCampaignId: {
    type: String,
    required: true,
    // default: undefined,
  },
  signPath: {
    type: String,
    required: true,
    // default: '/campaigns',
  },
  redirectPath: {
    type: String,
    required: true,
    // default: undefined,
  },
  disableNewsletterOption: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const suggestedEmail = computed<string>(() => {
  if (state.value.email && signFormSchema.shape.email.safeParse(state.value.email).success) {
    const suggestion = emailSpellChecker.run({ email: state.value.email })
    return suggestion?.full || ''
  }
  else {
    return ''
  }
})

const { showError } = useNotifications()

async function signPetition() {
  is_loading.value = true

  const phone = state.value.phone ? parsePhoneNumberWithError(state.value.phone, { defaultCountry: data.locale }) : undefined

  state.value.first_name = state.value.first_name!.trim()
  state.value.last_name = state.value.last_name!.trim()

  if (!props.sfCampaignId) return console.error('sf_campaign_id is undefined')
  if (!props.redirectPath) return console.error('redirect_path is undefined')

  const { event, event_source_url, fbc, fbp, meta_event_time, event_id } = useTracker()

  const postBody: CreateUserSchema = {
    first_name: state.value.first_name,
    last_name: state.value.last_name,
    email: state.value.email!,
    phone: phone ? phone.number : undefined,
    newsletter: state.value.newsletter!,
    sf_campaign_id: props.sfCampaignId,
    meta_event_time,
    event_id,
    fbc,
    fbp,
    event_source_url,
    email_id: petition.confirm_email_id,
    utm_source: user.utm_source,
    utm_campaign: user.utm_campaign,
    utm_medium: user.utm_medium,
    utm_content: user.utm_content,
    utm_term: user.utm_term,
  }

  const { api_endpoint } = useRuntimeConfig().public

  try {
    const res = await $fetch<AnimaResponse>(`${api_endpoint}${props.signPath}`, {
      method: 'POST',
      body: postBody,
    })

    if (res.success === true) {
      event({
        meta: 'petition_signed',
        google: 'petition_signed',
        snapchat: 'SIGN_UP',
        tiktok: 'CompleteRegistration',
      }, {
        first_name: state.value.first_name,
        last_name: state.value.last_name,
        email: state.value.email,
        phone: phone ? phone.number : undefined,
      })

      query.origin = 'petition'
      const query_user_info = objectToQuery({ ...state.value, ...query })
      await navigateTo(`${props.redirectPath}?${query_user_info}&sign-success`)
    }
    else {
      setTimeout(function () {
        showError()
        is_loading.value = false
      }, 1000)
      return
    }
  }
  catch (error) {
    showError()
    console.error('Error signing petition:', error)
  }
  finally {
    is_loading.value = false
  }
}

onMounted(() => {
  if (query.first_name) state.value.first_name = query.first_name as string
  if (query.last_name) state.value.last_name = query.last_name as string
  if (query.phone) state.value.phone = query.phone as string
  if (query.email) state.value.email = query.email as string
})
</script>
